import type { ReactNode } from 'react';
import { createElement, Fragment } from 'react';

export function formatTicketId(id: string): string {
  const short = id.replace(/-/g, '').slice(-4).toUpperCase();
  return `#TKT-${short}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function isSlaNearBreach(deadline: string | null): boolean {
  if (!deadline) return false;
  const ms = new Date(deadline).getTime() - Date.now();
  return ms > 0 && ms <= 4 * 60 * 60 * 1000;
}

export function slaElapsedPct(createdAt: string, deadline: string | null): number {
  if (!deadline) return 0;
  const start = new Date(createdAt).getTime();
  const end = new Date(deadline).getTime();
  const now = Date.now();
  if (end <= start) return 100;
  return Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100)));
}

export function slaTimeRemaining(deadline: string | null): string {
  if (!deadline) return '—';
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms <= 0) return 'Overdue';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function userInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

/** Staff creator when present; otherwise the customer contact captured at intake. */
export function ticketCreatorName(ticket: {
  createdBy: { firstName: string; lastName: string } | null;
  contactName: string | null;
}): string {
  if (ticket.createdBy) {
    const name = `${ticket.createdBy.firstName} ${ticket.createdBy.lastName}`.trim();
    if (name) return name;
  }
  const contact = ticket.contactName?.trim();
  if (contact) return contact;
  return '—';
}

export function fileIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return '🖼';
  if (mimeType.startsWith('video/')) return '🎬';
  if (mimeType.includes('pdf')) return '📄';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return '📊';
  if (mimeType.includes('word') || mimeType.includes('document')) return '📝';
  return '📎';
}

const NAMED_MENTION_RE = /@\[([^\]]+)\]\([^)]+\)/g;

export function renderMentionContent(content: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  for (const match of content.matchAll(NAMED_MENTION_RE)) {
    const index = match.index ?? 0;
    if (index > last) {
      nodes.push(createElement(Fragment, { key: `t-${last}` }, content.slice(last, index)));
    }
    nodes.push(
      createElement('span', { key: `m-${index}`, className: 'mention' }, `@${match[1]}`),
    );
    last = index + match[0].length;
  }
  if (last < content.length) {
    nodes.push(createElement(Fragment, { key: `t-${last}` }, content.slice(last)));
  }
  return nodes;
}
